import { Crisp } from "crisp-sdk-web";

import { CRISP_WEBSITE_ID } from "@/constants/global-constants";

export type SupportChatContext = {
  sessionToken?: string;
  myShopifyDomain: string;
  ownerEmail: string;
  shopifyPlan: string;
  fullName: string;
  sessionUserEmail: string;
  isOwner: boolean;
  isCollaborator: boolean;
  isEarlyUser: boolean;
  aiodPlan: string;
  freeTrialStatus: string;
  appVariantId: string;
};

export type SupportChatAvailabilityListener = (isAvailable: boolean) => void;

declare global {
  interface Window {
    CRISP_READY_TRIGGER?: () => void;
  }
}

let isConfigured = false;
let isLoaded = false;
let activeSessionToken: string | undefined;
let isAvailabilityMonitoring = false;
let isOfflineMessageMonitoring = false;
let currentSupportAvailability: boolean | undefined;
let availabilityReadyTrigger: (() => void) | undefined;
let previousReadyTrigger: (() => void) | undefined;
const availabilityListeners = new Set<SupportChatAvailabilityListener>();

const SUPPORT_OFFLINE_MESSAGE =
  "We're currently offline, we'll reply here as well as to your email once we're back online 😊";
const SUPPORT_NUDGE_MESSAGE = "Need help?";
const SUPPORT_ERROR_COUNT_STORAGE_KEY =
  "support-chat:consecutive-form-error-count";
const SHOP_LOGIN_EVENT_NAME = "shopify_store_login";
let pendingShopLoginDomain: string | undefined;
let fallbackSupportErrorCount = 0;

export type SupportErrorContext = {
  discountId: string;
  template: string;
  errors: string[];
};

function getSupportErrorMessage({
  discountId,
  template,
  errors,
}: SupportErrorContext) {
  return [
    "I need help with a discount error.",
    `Discount ID: ${discountId}`,
    `Template: ${template}`,
    "Errors shown:",
    ...errors.map((error) => `- ${error}`),
  ].join("\n");
}

function getSupportErrorCount() {
  if (typeof window === "undefined") return fallbackSupportErrorCount;

  try {
    const storedCount = Number(
      window.sessionStorage.getItem(SUPPORT_ERROR_COUNT_STORAGE_KEY),
    );

    return storedCount === 1 || storedCount === 2 ? storedCount : 0;
  } catch {
    return fallbackSupportErrorCount;
  }
}

function setSupportErrorCount(count: number) {
  fallbackSupportErrorCount = count;

  if (typeof window === "undefined") return;

  try {
    if (count === 0) {
      window.sessionStorage.removeItem(SUPPORT_ERROR_COUNT_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        SUPPORT_ERROR_COUNT_STORAGE_KEY,
        String(count),
      );
    }
  } catch {
    // The in-memory fallback still preserves the sequence when storage is blocked.
  }
}

/** Queues one Crisp profile event for the newly entered Shopify store. */
export function queueSupportChatShopLogin(myShopifyDomain: string) {
  console.debug("support.chat.shop.login", undefined, "Crisp: new store login");
  pendingShopLoginDomain = myShopifyDomain.trim().toLowerCase() || undefined;
}

function ensureConfigured() {
  if (typeof window === "undefined") return false;

  if (!isConfigured) {
    Crisp.configure(CRISP_WEBSITE_ID, {
      autoload: false,
      safeMode: true,
    });
    isConfigured = true;
  }

  startOfflineMessageMonitoring();

  return true;
}

/** Returns the current support availability once the Crisp client is ready. */
export function getSupportChatAvailability(): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  if (typeof $crisp === "undefined" || typeof $crisp.is !== "function") {
    return undefined;
  }

  return $crisp.is("website:available");
}

function showOfflineMessageIfNeeded() {
  const isAvailable =
    getSupportChatAvailability() ?? currentSupportAvailability;

  if (isAvailable === false) {
    Crisp.message.showText(SUPPORT_OFFLINE_MESSAGE);
  }
}

function startOfflineMessageMonitoring() {
  if (isOfflineMessageMonitoring) return;

  try {
    Crisp.message.onMessageSent(showOfflineMessageIfNeeded);
    isOfflineMessageMonitoring = true;
  } catch (error) {
    console.warn(
      "support.chat.offline-message.monitoring.failed",
      { reason: error instanceof Error ? error.message : String(error) },
      "Support chat offline message monitoring failed",
    );
  }
}

function publishSupportChatAvailability(isAvailable: boolean, force = false) {
  if (!force && currentSupportAvailability === isAvailable) return;

  currentSupportAvailability = isAvailable;
  availabilityListeners.forEach((listener) => listener(isAvailable));
}

function restorePreviousReadyTrigger() {
  if (
    availabilityReadyTrigger &&
    window.CRISP_READY_TRIGGER === availabilityReadyTrigger
  ) {
    window.CRISP_READY_TRIGGER = previousReadyTrigger;
  }

  availabilityReadyTrigger = undefined;
  previousReadyTrigger = undefined;
}

function startAvailabilityMonitoring() {
  if (isAvailabilityMonitoring || !ensureConfigured()) return false;

  isAvailabilityMonitoring = true;
  Crisp.onWebsiteAvailabilityChanged(publishSupportChatAvailability);

  const currentAvailability = getSupportChatAvailability();
  if (currentAvailability !== undefined) {
    publishSupportChatAvailability(currentAvailability, true);
    return true;
  }

  previousReadyTrigger = window.CRISP_READY_TRIGGER;
  availabilityReadyTrigger = () => {
    previousReadyTrigger?.();

    const readyAvailability = getSupportChatAvailability();
    if (readyAvailability !== undefined) {
      publishSupportChatAvailability(readyAvailability, true);
    }
  };
  window.CRISP_READY_TRIGGER = availabilityReadyTrigger;

  return true;
}

function stopAvailabilityMonitoring() {
  if (!isAvailabilityMonitoring) return;

  isAvailabilityMonitoring = false;
  Crisp.offWebsiteAvailabilityChanged();
  restorePreviousReadyTrigger();
}

/**
 * Subscribes to current and future support availability. The current value is
 * read after Crisp is ready, then kept up to date through Crisp's change event.
 */
export function subscribeToSupportChatAvailability(
  listener: SupportChatAvailabilityListener,
) {
  const wasMonitoring = isAvailabilityMonitoring;
  availabilityListeners.add(listener);
  startAvailabilityMonitoring();

  if (wasMonitoring && currentSupportAvailability !== undefined) {
    listener(currentSupportAvailability);
  }

  return () => {
    availabilityListeners.delete(listener);
    if (availabilityListeners.size === 0) stopAvailabilityMonitoring();
  };
}

/**
 * Loads Crisp and keeps its identity and custom session data aligned with the
 * current Shopify staff user. Changing users safely unbinds the old session.
 */
export function syncSupportChat(context: SupportChatContext) {
  // Never create an anonymous Crisp session while the server-derived identity
  // is still being restored from the cached Shopify user.
  const nextSessionToken = context.sessionToken?.trim() || undefined;
  if (!nextSessionToken) return;

  if (!ensureConfigured()) return;

  try {
    const tokenChanged = activeSessionToken !== nextSessionToken;

    if (tokenChanged) {
      Crisp.setTokenId(nextSessionToken);

      // Crisp only applies a token changed after injection on the next session.
      if (isLoaded) Crisp.session.reset();

      activeSessionToken = nextSessionToken;
    }

    if (context.sessionUserEmail) {
      Crisp.user.setEmail(context.sessionUserEmail);
    }
    if (context.fullName) Crisp.user.setNickname(context.fullName);
    if (context.myShopifyDomain) {
      Crisp.user.setCompany(context.myShopifyDomain, {
        url: `https://${context.myShopifyDomain}`,
      });
    }

    Crisp.session.setData({
      myshopify_domain: context.myShopifyDomain,
      owner_email: context.ownerEmail,
      shopify_plan: context.shopifyPlan,
      full_name: context.fullName,
      session_user_email: context.sessionUserEmail,
      is_owner: context.isOwner,
      is_collaborator: context.isCollaborator,
      is_early_user: context.isEarlyUser,
      aiod_plan: context.aiodPlan,
      free_trial_status: context.freeTrialStatus,
      App: context.appVariantId,
    });

    Crisp.load();
    isLoaded = true;

    const normalizedShopDomain = context.myShopifyDomain.trim().toLowerCase();
    if (
      pendingShopLoginDomain &&
      pendingShopLoginDomain === normalizedShopDomain
    ) {
      Crisp.session.pushEvent(SHOP_LOGIN_EVENT_NAME, {
        myshopify_domain: normalizedShopDomain,
      });
      pendingShopLoginDomain = undefined;
    }
  } catch (error) {
    console.warn(
      "support.chat.sync.failed",
      { reason: error instanceof Error ? error.message : String(error) },
      "Support chat context sync failed",
    );
  }
}

/** Opens the configured support chat. Commands can be queued before Crisp finishes loading. */
export function openSupportChatWidget(callback?: (opened: boolean) => void) {
  // React may pass its click event when this function is used directly as an
  // onClick handler. Only treat an actual function as the completion callback.
  const notify = typeof callback === "function" ? callback : undefined;

  if (!ensureConfigured()) {
    notify?.(false);
    return;
  }

  try {
    Crisp.chat.open();
    isLoaded = true;
    notify?.(true);
  } catch (error) {
    console.warn(
      "support.chat.open.failed",
      { reason: error instanceof Error ? error.message : String(error) },
      "Support chat failed to open",
    );
    notify?.(false);
  }
}

/** Shows a small support prompt without opening the chat or starting a conversation. */
export function giveSupportNudge() {
  if (!ensureConfigured()) return;

  try {
    Crisp.message.showText(SUPPORT_NUDGE_MESSAGE);
  } catch (error) {
    console.warn(
      "support.chat.nudge.failed",
      { reason: error instanceof Error ? error.message : String(error) },
      "Support chat nudge failed to display",
    );
  }
}

/**
 * Escalates repeated form errors once per successful-save cycle: nudge on the
 * first occurrence, then send one support message on the second occurrence.
 */
export function handleSupportErrorOccurrence(context: SupportErrorContext) {
  const errorCount = getSupportErrorCount();

  if (errorCount === 0) {
    setSupportErrorCount(1);
    giveSupportNudge();
    return;
  }

  if (errorCount === 1) {
    sendSupportChatMessage(getSupportErrorMessage(context), (sent) => {
      if (sent) setSupportErrorCount(2);
    });
  }
}

/** Starts a fresh error-support sequence after a confirmed successful save. */
export function resetSupportErrorSequence() {
  setSupportErrorCount(0);
}

/** Sends the visitor's exact text to Crisp, then opens the conversation. */
export function sendSupportChatMessage(
  message: string,
  callback?: (sent: boolean) => void,
) {
  if (!ensureConfigured()) {
    callback?.(false);
    return;
  }

  try {
    Crisp.message.sendText(message);
    Crisp.chat.open();
    isLoaded = true;
    callback?.(true);
  } catch (error) {
    console.warn(
      "support.chat.message.send.failed",
      { reason: error instanceof Error ? error.message : String(error) },
      "Support chat message failed to send",
    );
    callback?.(false);
  }
}
